<script setup>
import Loading from '@/components/loading.vue';
import { get_option, get_trait } from '@/services/packages';
import { create_assessment_progress, get_trait_assessment } from '@/services/assessment';
import { ref, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import childInfoCard from '@/components/childInfoCard.vue';
import { retrieve_child, send_request } from '@/services/child';
import { swalError, swalSuccess, confettiClass } from '@/helper';
import disabledCard from '@/components/disabledCard'
import { diff_duration } from '@/helper';

import { useStore } from 'vuex';

const store = useStore()
const route = useRoute()
const trait_config = ref()
const isLoading = ref(true)
const assessments = ref([])
const child = ref({})
const age_group_term = ref({})
onMounted(async ()=>{
  await store.dispatch('rewards/fetchTotalPoints')
  let response = await get_trait(route.params.id)
  let child_response = await retrieve_child(route.params.child_id)
  child.value = child_response.result.data.data
  trait_config.value = response.result.data
  let assessment_response = await get_trait_assessment({trait: route.params.id, child: route.params.child_id})
  if (assessment_response.error){
    swalError(assessment_response.result.error, true)
    return
  }
  await get_assessment_interval()
  assessments.value = parseAssessment(assessment_response.result.assessments)
  age_group_term.value = assessment_response.result.terms
  isLoading.value = false
})

function isLevelUp(){
  let completedAssesment = 0
  assessments.value.forEach((assessment)=>{
    let completedIndicator = 0
    assessment.indicators.forEach((indicator)=>{
      if (indicator){
        if ("checked" in indicator){
          if (indicator.checked == true){
            completedIndicator++
          }
        }
      }
    })
    if (completedIndicator ==  assessment.indicators.length){
      completedAssesment++
    }
  })
  return completedAssesment < assessments.value.length ? true : false
}

function needAssessmentReset(jsonData){
  // check if reset assessment is needed
  let assessment_progresses = jsonData.included.filter(item => {
    if (item.type == 'assessment_progress'){
      return item
    }
  })
  assessment_progresses.sort((a, b) => parseInt(b.id) - parseInt(a.id));
  const latest_assessment = assessment_progresses.shift()
  if (!latest_assessment){
    return false
  }
  const latest_date = latest_assessment.attributes.created_at
  const duration = diff_duration(latest_date, '', 'days')
  if (duration > assessment_interval.value.name){
    return true
  }
  return false
}

function parseAssessment(jsonData){
  const resetAssessment = needAssessmentReset(jsonData)
  // count check if reset assessment is false because first assessment could not has less count than next assessment
  let countCheck = 0 
  // Create a mapping to store assessments and their associated indicators
  const assessmentMap = new Map();
  // Iterate over assessments and include associated indicators
  jsonData.data.forEach(assessment => {
    const assessmentId = assessment.id;
    let indicators = jsonData.included.filter(item => {
      return item.type === 'indicator' && item.relationships.assessment.data.id === assessmentId;
    });
    
    const assessment_progresses = jsonData.included.filter(item => {
      if (item.type == 'assessment_progress' && item.relationships.assessment.data.id == assessmentId){
        item.attributes.score = JSON.parse(item.attributes.score);
        return item
      }
    })
    assessment_progresses.sort((a, b) => parseInt(b.id) - parseInt(a.id));

    let parent_progress = assessment_progresses[0]
    let latest_score = []
    if (parent_progress){
      latest_score = parent_progress.attributes.score
    }
    if (latest_score.length !== 0){
      indicators = indicators.map(item => {
        if (resetAssessment){
          return {
            ...item,
            checked: false
          }
        }else{
          if (countCheck){
            return {
              ...item,
              checked: false
            }
          }else{
            return {
              ...item,
              checked: latest_score.find(ind_id => ind_id == item.id) ? true : false
            }
          }
        }
      })
      countCheck = indicators.filter(item => item.checked == true).length == indicators.length ?  0 : 1
    }
    // Store the assessment and associated indicators in the map
    assessmentMap.set(assessmentId, {
      assessment: {...assessment},
      assessment: {
        ...assessment, 
        parent_progress: parent_progress ? parent_progress.id : null
      },
      indicators: indicators
    });
  });

  return Array.from(assessmentMap.values());
}

const currenAssessmentInd = ref('')
const open = ref(false)
async function set_progress(assessmentInd){
  if (assessmentInd == 0){
    setIndicator(assessmentInd)
  }else{
    let counter = 0
    let previousAssessmentObj = assessments.value[assessmentInd - 1]
    if (previousAssessmentObj.indicators.length !== 0){
      previousAssessmentObj.indicators.map(item => {
        if (item.checked){
          counter++
        }
      })
      if (counter != previousAssessmentObj.indicators.length){
        Swal.fire({
          title: 'Warning',
          text: 'Please complete previous assessment before moving on',
          icon: 'warning'
        })
      }else{
        setIndicator(assessmentInd)
      }
    }
  }
}

const isCompleted = ref(false)
function setIndicator(ind){
  open.value = true
  currenAssessmentInd.value = ind
  const { indicators } = assessments.value[ind]
  selectedIndicator.value = []
  let counter = 0
  indicators.map(item => {
    if (item.checked){
      counter++
      selectedIndicator.value.push(item.id)
    }
  })
  if (counter == indicators.length){
    isCompleted.value = true
  }else{
    isCompleted.value = false
  }
}

function clickIndicator(){
  if (isCompleted.value){
    Swal.fire({
      title: 'Error',
      text: 'Assessment has been completed. Could not be edit',
      icon: 'warning'
    })    
  }
}

const selectedIndicator = ref([])
async function updateAssessment(assessmentInd){
  isCompleted.value = true
  const assessment_progress = {
    assessment_id: '',
    child_id: '',
    is_completed: false,
    score: 0,
    is_active: true,
    parent_progress: null
  }

  const {assessment, indicators} = assessments.value[assessmentInd]
  assessment_progress.assessment_id = assessment.id
  assessment_progress.child_id = child.value.id
  assessment_progress.score = JSON.stringify(selectedIndicator.value)
  assessment_progress.parent_progress = assessment.parent_progress
  if (indicators.length == selectedIndicator.value.length){
    assessment_progress.is_completed = true
    assessment_progress.is_active = false
  }else{
    assessment_progress.is_completed = false
  }
  let response = await create_assessment_progress(assessment_progress)
  if (response?.error){
    swalError('An error occur. Please contact admin')
  }else{
    let assessment_id_response = response.result.data.data.relationships.assessment.data.id
    let tempAssessment = assessments.value.find((assessment)=> assessment.assessment.id == assessment_id_response)
    let completedIndRes = JSON.parse(response.result.data.data.attributes.score)
    completedIndRes.forEach((item)=>{
      let tempIndicator = tempAssessment.indicators.find(x => x.id == item)
      if (tempIndicator){
        tempIndicator.checked = true
      }
      setIndicator(assessmentInd)
    })
    
    // Update points
    store.dispatch('rewards/fetchTotalPoints')
    
    swalSuccess('Assessment progress updated', false, 'Updated')
  }
  isCompleted.value = false
  if (assessment_progress.is_completed){
    confettiClass.addConfetti()
  }
}

function completedIndicator(indicators){
  return indicators.filter(item => item.checked == true).length
}

async function requestLevelUp(){
  let response = await send_request(
    child.value.id, trait_config.value.id
  )
  if (!response.error){
    swalSuccess('Level up request has been send')
  }else{
    swalError('Error occur during request')
  }
}

function checkedPreviousAsessment(ind){
  if (ind == 0){
    return false
  }
  let counter = 0
  let previousAssessmentObj = assessments.value[ind - 1]
  if (previousAssessmentObj.indicators.length !== 0){
    previousAssessmentObj.indicators.map(item => {
      if (item.checked){
        counter++
      }
    })
    if (counter == previousAssessmentObj.indicators.length){
      return false
    }else{
      return true
    }
  }else{
    return false
  }
}

const assessment_interval = ref({})
async function get_assessment_interval(){
  let response = await get_option('assessment_interval')
  let text_response = await get_option('assessment_interval_text')
  if(response.error){
    swalError(response.result, true)
    return
  }
  if(text_response.error){
    swalError(text_response.result, true)
    return
  }
  assessment_interval.value.val = response.result.data.attributes.name
  assessment_interval.value.text = text_response.result.data.attributes.name
  assessment_interval.value.full_text = assessment_interval.value.text.replace('%days%', assessment_interval.value.val)
}
</script>

<template>
  <div class="container-fluid">
    <Breadcrumbs main="Assessment"/>
    
    <Loading v-if="isLoading" />
    <div class="container" v-else>
      <div class="js-container temp-container" style="top:0px !important;"></div>
      <!-- Child Info -->
      <childInfoCard class="col-md-6" :child="child"/>

      <div class="row">
        <div class="col-md-12">
          <div class="card">
            <div class="card-body">
              {{ assessment_interval.full_text || 'Error retrieving information' }}
            </div>
          </div>
        </div>
        <!-- Assessment -->
        <div :class="{'col-md-8': open, 'col-md-12': !open}">
          
          <div class="card">
            <!-- Grey card if subscription exprired -->
            <disabledCard :child_id="child.id" />
            
            <div class="card-header pb-2 pt-4 d-flex align-items-center justify-content-between flex-wrap">
              <h5 class="mb-0">
                <span class="text-secondary">Trait Name : </span>
                {{ trait_config?.attributes.name }}
              </h5>
              <div class="d-flex flex-wrap">
                <!-- <div class="chart-card-info me-2">
                  <p class="mb-0">
                    Current Age Group :
                    <span>{{ age_group_term?.name || '-' }}</span>
                  </p>
                </div> -->
                <div class="d-flex">
                  <button class="btn btn-light-success" 
                    :disabled="isLevelUp()"
                    @click="requestLevelUp()"
                  >
                    Request for Level Up
                  </button>
                  <router-link class="ms-2 btn btn-light-warning" 
                    :to="{
                      name: 'support-ticket',
                      query: {
                        topic: 'Help in ' + trait_config.attributes.name,
                        subject: child.attributes.name + ' - Assessment'
                      }
                    }"
                  >
                    Request for Help
                  </router-link>
                </div>
              </div>
            </div>

            <div class="card-body">
              <emptyCard v-if="assessments.length == 0" />
              <div v-else class="assessment-table table-responsive">
                <table class="table">
                  <thead>
                    <tr>
                      <th scope="col">
                        <div>
                          <p>Code</p>
                        </div>
                      </th>
                      <th scope="col">
                        <div>
                          <p class="text-left">Assessment Description</p>
                        </div>
                      </th>
                      <th scope="col" v-if="!open">
                        <div>
                          <p>Score</p>
                        </div>
                      </th>
                      <th scope="col">
                        <div>
                          <p>Action</p>
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(assessmentObj, ind) in assessments">
                      <td>
                        <div>
                          <p>{{ assessmentObj.assessment.attributes.code }}</p>
                        </div>
                      </td>
                      <td>
                        <div>
                          <p>{{ assessmentObj.assessment.attributes.name }}</p>
                        </div>
                      </td>
                      <td v-if="!open">
                        <div>
                          <p>{{ completedIndicator(assessmentObj.indicators) }} / {{ assessmentObj.indicators.length }}</p>
                        </div>
                      </td>
                      <td class="action-body-col">
                        <button v-if="!open || currenAssessmentInd !== ind" 
                          class="p-0 action-button" 
                          :class="checkedPreviousAsessment(ind)? 'disabled' : ''"
                          @click="set_progress(ind)"
                        >
                          <vue-feather type="chevron-right"></vue-feather>
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

          </div>
        </div>
        
        <!-- Indicator -->
        <div v-if="open" class="col-md-4 card">
          <div class="card-body">
            <div class="row justify-content-end">
              <button
                class="float-end p-0 btn btn-light-danger icon-btn close" 
                @click="open = false, currenAssessmentInd = ''"
              >
                <vue-feather type="x"></vue-feather>
              </button>
            </div>
            <div v-for="indicator in assessments[currenAssessmentInd].indicators" :key="indicator.id" class="row mt-2">
              <div class="col-10 item-border">
                <p>{{ indicator.attributes.name }}</p>
              </div>
              <div class="col-2 d-flex align-items-center">
                <div class="round"
                  @click="clickIndicator()"
                >
                  <input type="checkbox" 
                    :id="indicator.id" 
                    :value="indicator.id" 
                    v-model="selectedIndicator" 
                    :disabled="isCompleted"
                    :class="isCompleted ? 'disabled' : ''"
                  />
                  <label :for="indicator.id"></label>
                </div>
              </div>
            </div>

            <div class="d-flex flex-row-reverse mt-3">
              <button @click="updateAssessment(currenAssessmentInd)" 
                class="btn btn-light-success"
                :disabled="isCompleted"
                :class="isCompleted ? 'disabled' : ''"
              >
                Save Record
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>